import { useState } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import { STATE_COLOR, formatDateTime, formatTime } from './enums';

// Material UI
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import { Divider } from '@mui/material';

export default function DischargeEventsCalendar(props) {
	const { locationID, eventsData, selectedYear, loading } = props;
	
	const [selectedDate, setSelectedDate] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);	

	const navigate = useNavigate();

	const yearMonths = Array.from({ length: 12 }, (_, i) => new Date(selectedYear, i, 1));

	// Convert each start_datetime to the user's preferred timezone
    const convertedEventsData = eventsData.map(event => {
        const convertedDate = formatDateTime(event.start_datetime);
        return { ...event, start_datetime_converted: convertedDate };
    });

	// Split the list of events into a dictionary where the key is the date
	const eventLookup = convertedEventsData.reduce((acc, event) => {
		const date = event.start_datetime_converted.split(',')[0];
		if (!acc[date]) {
			acc[date] = [];
		}
		acc[date].push(event);
		return acc;
	}, {});

	function convertDateTimeToLinkString(dateTime) {
		return dateTime.replace('-', '').replace('-', '').replace('T', '').replace(':', '').replace(':', '').replace('Z', '');
	}

	function getEventTypeIcon(eventTypeCode) {
	
		switch (eventTypeCode) {
			case 0:
				return <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
			case 1:
				return <ArrowCircleDownOutlinedIcon sx={{ color: STATE_COLOR.CRITICAL }}/>
			case 2:
				return <ArrowCircleUpOutlinedIcon sx={{ color: STATE_COLOR.WITHIN_LIMITS }}/>
			default:
				return <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
		}
	}

	function handleClose() {
		setDialogOpen(false);
	};

	function handleEventTimeClick(eventDateTime) {
		handleClose();
		
		const dischargeEvent = convertDateTimeToLinkString(eventDateTime);
		navigate(`/location/${locationID}/discharges/${dischargeEvent}/`);
	};

	function Month({ month }) {

		const startOfMonth = new Date(selectedYear, month.getMonth(), 1);
		const endOfMonth = new Date(selectedYear, month.getMonth() + 1, 0);

		let calendarDays = [];
		
		// Add days from the previous month
		for (let i = startOfMonth.getDay(); i > 0; i--) {
			calendarDays.push({
				dayDate: new Date(selectedYear, month.getMonth(), -i + 1),
				isThisMonth: false,
			});
		}
		
		// Add days of the current month
		for (let i = 1; i <= endOfMonth.getDate(); i++) {
			calendarDays.push({
				dayDate: new Date(selectedYear, month.getMonth(), i),
				isThisMonth: true,
			});
		}
		
		// Add days from the next month
		let extraDays = 1;
		while (calendarDays.length < 42) { // 42 days = 6 weeks * 7 days
			calendarDays.push({
				dayDate: new Date(selectedYear, month.getMonth() + 1, extraDays++),
				isThisMonth: false,
			});
		}

		return (
			<div style={{ border: '1px solid #D8D7D6', borderRadius: '10px', margin: '10px', overflow: 'hidden', boxShadow: '2px 2px 4px gray' }}>
				<Typography
					variant="h6"
					align="center"
					fontFamily='Saira, sans-serif'
					fontWeight='bold'
					sx={{
						backgroundColor: '#B1282E',
						color: 'white',
						padding: '10px',
					}}
				>
					{month.toLocaleString('default', { month: 'long' })}
				</Typography>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
									<TableCell
										key={i}
										align="center"
										sx={{
											fontFamily: 'Saira,sans-serif',
											fontWeight: 'bold',
											textAlign: 'center',
											p: '5px',
											backgroundColor: ((i % 7 === 0) || (i % 7 === 6)) ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
										}}
									>
										{day}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{Array.from({ length: 6 }, (_, i) => (
								<TableRow key={i}>
									{calendarDays.slice(i * 7, (i + 1) * 7).map((dayObject, j) => (
										<TableCell
											key={j}
											align="center"
											sx={{
												backgroundColor: ((j % 7 === 0) || (j % 7 === 6)) ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
    											borderLeft: (j % 7 !== 0) ? '1px solid rgba(224, 224, 224, 1)' : 'none',
												borderRight: (j % 7 !== 6) ? '1px solid rgba(224, 224, 224, 1)' : 'none',
											}}
										>
											{dayObject && 
												<Day
													day={dayObject.dayDate.getDate()}
													month={dayObject.dayDate.getMonth()}
													year={dayObject.dayDate.getFullYear()}
													isCurrentMonth={dayObject.isThisMonth}
												/>
											}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</div>
		);
	}

	function Day({ day, month, year, isCurrentMonth }) {

		const fullDate = new Date(year, month, day);
		const fullDateString = fullDate.toLocaleString(undefined, { month: '2-digit', day: '2-digit', year:'numeric' });
		const dayEvents = eventLookup[fullDateString] || [];
		const hasEvent = dayEvents.length > 0 && day != null;

		// Make sure that there are events for the day before allowing the user to click on it
		const handleClickCalendarDay = () => {
			if (dayEvents.length > 0) {
				setSelectedDate(fullDate);
				setDialogOpen(true);
			}
		};

		let dayStyle = {};
		if (isCurrentMonth) {
			dayStyle = ({
				backgroundColor: hasEvent ? '#B1282E' : 'transparent',
				color: hasEvent ? 'white' : 'black',
				cursor: hasEvent ? 'pointer' : 'default',
				'&:hover': {
					transform: hasEvent ? 'translate(-53%, -53%)' : 'translate(-50%, -50%)',
					boxShadow: hasEvent ? '2px 2px 3px #0F1820' : 'none',
					backgroundColor: hasEvent ? '#B1282E99' : 'inherit',
				},
				'&:active': {
					transform: hasEvent ? 'translate(-47%, -47%)' : 'translate(-50%, -50%)',
					boxShadow: hasEvent ? '-1px -1px 2px #0F1820': 'none',
					backgroundColor: hasEvent ? 'rgb(128, 11, 16)' : 'inherit',
				},
			});
		} else {
			dayStyle = ({
				backgroundColor: hasEvent ? 'rgba(178, 41, 46, 0.25)' : 'transparent',
				color: hasEvent ? 'white' : 'rgba(0, 0, 0, 0.25)',
			});
		}

		return (
			<div
				style={{ 
					width: '100%',
					position: 'relative', // Needed to position the inner content
					boxSizing: 'border-box', 
					textAlign: 'center',
				}}
				onClick={ isCurrentMonth ? handleClickCalendarDay : () => null}
			>
				<Typography 
					align="center"
					fontFamily='Saira,sans-serif'
					fontWeight='500'
					sx={{
						position: 'absolute', // Position the content in the center of the square cell
						top: '50%',
						left: '50%',
						width: '30px',
						height: '30px',
						display: 'flex',
						transform: 'translate(-50%, -50%)',
						alignItems: 'center',
						justifyContent: 'center',
						borderRadius: '50%',
						...dayStyle,
					}}
				>
					{day}
				</Typography>
			</div>
		);
	}

	function EventsDialog() {
		return (
			<Dialog onClose={handleClose} open={dialogOpen}>
				<DialogTitle
					sx={{
						fontFamily: 'Saira, sans-serif',
						fontWeight: 'bold',
						textAlign: 'center',
						color: 'white',
						backgroundColor: '#0F1820',
						p: 1,
					}}
				>
					{selectedDate?.toLocaleDateString('default', { year: 'numeric', month: 'long', day: 'numeric' })}
				</DialogTitle>
				<Divider />
				<DialogContent sx={{ p: 1 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ p: 0 }} />
								<TableCell
									sx={{
										fontFamily: 'Saira, sans-serif',
										fontWeight: 'bold',
										textAlign: 'center',
										color: '#0F1820',
										pt: 0.5,
										pb: 0
									}}
								>
									Start Time
								</TableCell>
								<TableCell
									sx={{
										fontFamily: 'Saira, sans-serif',
										fontWeight: 'bold',
										textAlign: 'center',
										color: '#0F1820',
										pt: 0.5,
										pb: 0
									}}
								>
									Duration
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{(eventLookup[selectedDate?.toLocaleString(undefined, { month: '2-digit', day: '2-digit', year:'numeric' })] || []).map((dEvent, i) => (
								<TableRow key={i} hover onClick={() => handleEventTimeClick(dEvent.start_datetime)}>
									<TableCell sx={{ pt: 1, pb: 0.5,}}>
										{getEventTypeIcon(dEvent.event_type)}
									</TableCell>
									<TableCell sx={{ fontFamily: 'Saira, sans-serif', py: 0}}>
										{dEvent.start_datetime_converted.split(',')[1]}
									</TableCell>
									<TableCell sx={{ fontFamily: 'Saira, sans-serif', py: 0}}>
										{dEvent.duration_text}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<div style={{ 
			display: 'grid', 
			gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
			gap: '2px'
		}}>
			{yearMonths.map((month, i) => (
				<div key={i}>
					<Month month={month} />
				</div>
			))}
			<EventsDialog />
		</div>
	);
};